import { createContext, useContext, useMemo } from 'react'
import { useLocation, useSearchParams } from 'react-router'
import { useMatches } from '../../router/hooks'
import { PageType, RouteCrumb, RouteGroup, RouteHandle } from '../../router/types'
import { BreadcrumbObjectType } from '../generated/apollo-operations'
import { useOrgProjects } from './useOrgProjects'

export type PageContextType = {
  objectId?: string
  objectType?: BreadcrumbObjectType
  pathname: string
  query: Record<string, string | undefined>
  pageType?: PageType
  group?: RouteGroup
  crumbs: RouteCrumb[]
  isPageWithLeftNavCollapsed: boolean
  isPageWithActivityFeed: boolean
  isPageWithBackLink: boolean
  isSharedPath: boolean
  isAuthPage: boolean
}

// Re-export for consumer convenience
export type { RouteCrumb, RouteGroup }

export function PageProvider({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation()
  const [searchParams] = useSearchParams()
  const matches = useMatches()

  const { selectedProject, projects } = useOrgProjects()

  // React Router's useMatches() returns matches in root → leaf order. This isn't
  // guaranteed in prose by the v7 API docs, but it's shown in the official
  // breadcrumb recipe (Remix v2 / react-router v7 docs) and has held in source
  // since v6. A future upgrade that changed this would reverse the order
  // visibly, not silently.
  const { allParams, handle, crumbs } = useMemo(
    () =>
      matches.reduce<{
        allParams: Record<string, string | undefined>
        handle: Partial<RouteHandle>
        crumbs: RouteCrumb[]
      }>(
        (acc, match) => {
          // Child handle fields override parent fields. Null/undefined values
          // are skipped so a child omitting a field doesn't clear the parent's.
          const definedHandleEntries = match.handle
            ? Object.entries(match.handle).filter(([, v]) => v != null)
            : []
          return {
            allParams: { ...acc.allParams, ...match.params },
            handle: { ...acc.handle, ...Object.fromEntries(definedHandleEntries) },
            crumbs: match.handle?.crumb
              ? [...acc.crumbs, { label: match.handle.crumb, pathname: match.pathname }]
              : acc.crumbs,
          }
        },
        { allParams: {}, handle: {}, crumbs: [] },
      ),
    [matches],
  )

  const query = useMemo(() => {
    const q: Record<string, string | undefined> = {}
    searchParams.forEach((value, key) => {
      q[key] = value
    })
    Object.assign(q, allParams)
    return q
  }, [allParams, searchParams])

  const pageType = handle.pageType
  const objectType = handle.objectType

  const objectId = useMemo(() => {
    if (pageType === 'home') {
      return selectedProject?.id || projects.at(0)?.id || ''
    }
    const {
      project_id,
      table_id,
      record_id,
      form_id,
      document_id,
      chart_id,
      workspace_id,
      pivot_table_id,
      workflow_id,
      embed_id,
    } = query
    return (
      project_id ||
      table_id ||
      record_id ||
      form_id ||
      workflow_id ||
      document_id ||
      chart_id ||
      workspace_id ||
      pivot_table_id ||
      embed_id ||
      ''
    )
  }, [pageType, query, selectedProject?.id, projects])

  const value = useMemo(
    () => ({
      pageType,
      objectId,
      objectType,
      pathname,
      query,
      group: handle.group,
      crumbs,
      isPageWithLeftNavCollapsed: handle.showLeftNav === false,
      isPageWithActivityFeed: handle.showActivityFeed ?? false,
      isPageWithBackLink: handle.showBackLink ?? false,
      isSharedPath: handle.isShared ?? false,
      isAuthPage: handle.isAuth ?? false,
    }),
    [pageType, objectId, objectType, pathname, query, handle, crumbs],
  )

  return <PageContext.Provider value={value}>{children}</PageContext.Provider>
}

export const usePage = () => {
  const context = useContext(PageContext)
  if (!context) {
    throw new Error('usePage must be used within a PageProvider')
  }
  return context
}

const PageContext = createContext<PageContextType | undefined>(undefined)
